import React, { useState, useEffect } from 'react';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  organization?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface ProfileFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  organization: string;
}

interface ProfileEditModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: ProfileFormData) => Promise<void>;
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<ProfileFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    organization: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [saveLoading, setSaveLoading] = useState(false);

  // ユーザー情報が変更された時にフォームデータを更新
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        display_name: user.display_name || '',
        organization: user.organization || ''
      });
    }
  }, [user]);

  // モーダルが開かれた時にエラーをクリア
  useEffect(() => {
    if (isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = '苗字は必須です';
    }
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = '名前は必須です';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setSaveLoading(true);
    setErrors({});
    
    try {
      await onSave(formData);
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'プロフィールの更新に失敗しました';
      setErrors({
        general: errorMessage
      });
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content modern-auth-modal">
          {/* ヘッダー */}
          <div className="modern-auth-header">
            <div className="auth-logo">
              <img 
                src="/img/splice_logo.png" 
                alt="SPLICE" 
                className="logo-image"
              />
            </div>
            <button
              type="button"
              className="modern-close-btn"
              onClick={onClose}
              disabled={saveLoading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* コンテンツ */}
          <div className="modern-auth-body">
            <div className="auth-title">
              <h2>プロフィール編集</h2>
            </div>

            {saveLoading ? (
              <div className="auth-loading">
                <div className="loading-spinner"></div>
                <span>保存中...</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                {/* エラーメッセージ */}
                {errors.general && (
                  <div className="auth-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33333L14.6667 14H1.33333L8 1.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V8.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 11.3333H8.00667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {errors.general}
                  </div>
                )}

                {/* 苗字 */}
                <div className="form-group">
                  <label htmlFor="last_name">苗字</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="田中"
                      className={errors.last_name ? 'error' : ''}
                      disabled={saveLoading}
                    />
                  </div>
                  {errors.last_name && <span className="field-error">{errors.last_name}</span>}
                </div>

                {/* 名前 */}
                <div className="form-group">
                  <label htmlFor="first_name">名前</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="太郎"
                      className={errors.first_name ? 'error' : ''}
                      disabled={saveLoading}
                    />
                  </div>
                  {errors.first_name && <span className="field-error">{errors.first_name}</span>}
                </div>

                {/* メールアドレス */}
                <div className="form-group">
                  <label htmlFor="email">メールアドレス</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M3 5.25L9 9.75L15 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 5.25H15C15.8284 5.25 16.5 5.92157 16.5 6.75V13.5C16.5 14.3284 15.8284 15 15 15H3C2.17157 15 1.5 14.3284 1.5 13.5V6.75C1.5 5.92157 2.17157 5.25 3 5.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="example@email.com"
                      className={errors.email ? 'error' : ''}
                      disabled={saveLoading}
                    />
                  </div>
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                {/* 所属組織 */}
                <div className="form-group">
                  <label htmlFor="organization">所属組織</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M3.75 13.5V6.75C3.75 6.55109 3.82902 6.36032 3.96967 6.21967C4.11032 6.07902 4.30109 6 4.5 6H7.5V4.5C7.5 4.30109 7.57902 4.11032 7.71967 3.96967C7.86032 3.82902 8.05109 3.75 8.25 3.75H9.75C9.94891 3.75 10.1397 3.82902 10.2803 3.96967C10.421 4.11032 10.5 4.30109 10.5 4.5V6H13.5C13.6989 6 13.8897 6.07902 14.0303 6.21967C14.171 6.36032 14.25 6.55109 14.25 6.75V13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 9.75H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10.5 9.75H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="会社名・学校名等"
                      disabled={saveLoading}
                    />
                  </div>
                </div>

                {/* 保存ボタン */}
                <button type="submit" className="auth-submit-btn" disabled={saveLoading}>
                  <span>保存</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditModal; 